import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from '@/store/auth'
import { ToastHost } from '@/store/toast'
import { installRetryTriggers } from '@/lib/voicesoap/uploadOrchestrator'
import LoginPage from '@/pages/LoginPage'
import PosPage from '@/pages/PosPage'
import ReceiptListPage from '@/pages/ReceiptListPage'
import ReceiptDetailPage from '@/pages/ReceiptDetailPage'
import VisitReviewPage from '@/pages/VisitReviewPage'
import VisitFinalPage from '@/pages/VisitFinalPage'
import VisitListPage from '@/pages/VisitListPage'
import VisitNewPage from '@/pages/VisitNewPage'
import VisitRecordPage from '@/pages/VisitRecordPage'
import PatientListPage from '@/pages/PatientListPage'
import PatientNewPage from '@/pages/PatientNewPage'
import PatientDetailPage from '@/pages/PatientDetailPage'
import ProductsPage from '@/pages/ProductsPage'
import CalendarPage from '@/pages/CalendarPage'
import DevAudioSamplesPage from '@/pages/DevAudioSamplesPage'
import AppShell from '@/components/AppShell'

function Guard({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAuth()
  const location = useLocation()
  if (!initialized) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500">
        กำลังโหลด…
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />
  return <AppShell>{children}</AppShell>
}

export default function App() {
  const init = useAuth((s) => s.init)
  useEffect(() => {
    void init()
  }, [init])

  // Voice→SOAP audio queue: drain pending uploads on mount and whenever the
  // network/tab becomes available again. iOS has no Background Sync so this
  // listener trio (`online`, `visibilitychange`, app mount) is how queued
  // recordings eventually reach the server.
  useEffect(() => {
    return installRetryTriggers()
  }, [])

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <Guard>
              <PosPage />
            </Guard>
          }
        />
        <Route
          path="/receipts"
          element={
            <Guard>
              <ReceiptListPage />
            </Guard>
          }
        />
        <Route
          path="/receipts/:id"
          element={
            <Guard>
              <ReceiptDetailPage />
            </Guard>
          }
        />
        <Route
          path="/patients"
          element={
            <Guard>
              <PatientListPage />
            </Guard>
          }
        />
        <Route
          path="/patients/new"
          element={
            <Guard>
              <PatientNewPage />
            </Guard>
          }
        />
        <Route
          path="/patients/:id"
          element={
            <Guard>
              <PatientDetailPage />
            </Guard>
          }
        />
        <Route
          path="/products"
          element={
            <Guard>
              <ProductsPage />
            </Guard>
          }
        />
        <Route
          path="/calendar"
          element={
            <Guard>
              <CalendarPage />
            </Guard>
          }
        />
        <Route
          path="/visits"
          element={
            <Guard>
              <VisitListPage />
            </Guard>
          }
        />
        <Route
          path="/visits/new"
          element={
            <Guard>
              <VisitNewPage />
            </Guard>
          }
        />
        <Route
          path="/visits/:id/record"
          element={
            <Guard>
              <VisitRecordPage />
            </Guard>
          }
        />
        <Route
          path="/visits/:id/review"
          element={
            <Guard>
              <VisitReviewPage />
            </Guard>
          }
        />
        <Route
          path="/visits/:id/final"
          element={
            <Guard>
              <VisitFinalPage />
            </Guard>
          }
        />
        <Route
          path="/dev/audio-samples"
          element={
            <Guard>
              <DevAudioSamplesPage />
            </Guard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastHost />
    </>
  )
}
